import { useEffect } from 'react'

/**
 * Browser-translation safety net.
 *
 * Chrome / Edge / Safari / Firefox all auto-translate page text. Without
 * protection, brand names and technical acronyms get mangled. Real-world
 * casualties observed on this site under French auto-translate:
 *
 *   MyDart   -> MaDart    (Google read "My" as English possessive)
 *   NEO      -> NEO       (sometimes accented to "NEO")
 *   Trackme  -> Suivez-moi (literal verb-phrase translation)
 *   Pentabay -> Pentabaie  (false French cognate)
 *
 * The HTML spec defines `translate="no"` for exactly this case. Every
 * major browser respects it, and so do Google Translate, DeepL widget,
 * Apple Live Text, and the LLM-powered "read page" assistants.
 *
 * This overlay applies the attribute in two passes, idempotently:
 *
 *  1. Tag whole elements that are known brand containers (product-card
 *     H3s, the pentagon SVG, dropdown rows, footer brand links, wordmark
 *     images whose alt-text carries the brand). Tagging the container
 *     protects all its descendants by attribute inheritance.
 *
 *  2. Walk remaining text nodes; for any node that mentions a brand
 *     mid-sentence ("Read MyDart spec", "PHIPA / HIPAA-aligned"), wrap
 *     just the brand token in <span translate="no"> so the surrounding
 *     prose stays translatable.
 *
 * Re-runs whenever the source page-mount scripts dispatch
 * `pb-pages-mounted` so subpages added later (services, healthcare,
 * insights, ...) get the same treatment.
 */
const BRAND_WORDS = [
  // Brand names - longest/most-specific first so the alternation prefers
  // PHIPA over PHI when both could match.
  'Pentabay',
  'WeGuard',
  'MyDart',
  'Trackme',
  'Zentra',
  'AVA',
  'NEO',
  // Technical acronyms specific to the regulated-software domain.
  'PIPEDA',
  'PHIPA',
  'HIPAA',
  'FHIR',
  'HL7',
  'PHI',
]

// Hoisted to module scope so we don't allocate a regex on every walk
// (the previous per-call factory showed up as a small but real cost
// when the page-mount event fires twice per page load).
const BRAND_PATTERN = '\\b(' + BRAND_WORDS.join('|') + ')\\b'
const BRAND_RE_TEST = new RegExp(BRAND_PATTERN)
const BRAND_RE_EXEC = new RegExp(BRAND_PATTERN, 'g')

const CONTAINER_SELECTORS = [
  // Product-card H3 ("MyDart", "WeGuard", ...)
  'h3.t-h2',
  // Wordmark images carry the brand in their alt attribute.
  '.product-wordmark',
  '.product-logo',
  // The pentagon SVG: every <text> label is a product name.
  '.scene-penta',
  '#penta',
  // Top-nav products dropdown rows.
  'details a[href*="pentabay.com/"]',
  'details a[href*="trackme.pentabay.com"]',
  // Footer brand list.
  'footer a[href*="pentabay.com/"]',
  'footer a[href*="trackme.pentabay.com"]',
  // <code>/<kbd>/<samp> should never translate per HTML semantics; some
  // browsers still try. Belt and suspenders.
  'code',
  'kbd',
  'samp',
]

function tagContainers(root) {
  for (const sel of CONTAINER_SELECTORS) {
    let nodes
    try {
      nodes = root.querySelectorAll(sel)
    } catch {
      continue
    }
    for (const el of nodes) {
      if (el.getAttribute('translate') !== 'no') {
        el.setAttribute('translate', 'no')
      }
    }
  }
}

function shouldSkipAncestor(el) {
  while (el) {
    if (el.getAttribute && el.getAttribute('translate') === 'no') return true
    if (el.hasAttribute && el.hasAttribute('data-hero-h1')) return true
    const tag = el.tagName
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEXTAREA') return true
    el = el.parentElement
  }
  return false
}

function wrapTextNodes(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT
      // Cheap stateless test - no lastIndex tracking needed.
      if (!BRAND_RE_TEST.test(node.nodeValue)) return NodeFilter.FILTER_REJECT
      if (shouldSkipAncestor(node.parentElement)) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const targets = []
  let n
  while ((n = walker.nextNode())) targets.push(n)
  for (const node of targets) {
    const txt = node.nodeValue
    const frag = document.createDocumentFragment()
    let lastIdx = 0
    BRAND_RE_EXEC.lastIndex = 0
    let m
    while ((m = BRAND_RE_EXEC.exec(txt)) !== null) {
      if (m.index > lastIdx) frag.appendChild(document.createTextNode(txt.slice(lastIdx, m.index)))
      const span = document.createElement('span')
      span.setAttribute('translate', 'no')
      span.appendChild(document.createTextNode(m[0]))
      frag.appendChild(span)
      lastIdx = m.index + m[0].length
    }
    if (lastIdx < txt.length) frag.appendChild(document.createTextNode(txt.slice(lastIdx)))
    if (frag.childNodes.length > 0 && node.parentNode) {
      node.parentNode.replaceChild(frag, node)
    }
  }
}

function protect(root) {
  if (!root) return
  // Only tag whole brand containers (product-card headings, wordmarks, the
  // pentagon SVG, nav/footer brand links, code/kbd). We deliberately do NOT
  // split brand tokens out of running prose any more: that fragmented every
  // sentence mentioning a brand ("Read the Pentabay guide") into three text
  // nodes, which stopped the site's own TranslationManager from matching the
  // full-sentence cache key - leaving that copy untranslated. Prose is now
  // translated whole (the container tags still keep product names intact).
  tagContainers(root)
}

export default function BrandProtector() {
  useEffect(() => {
    function run() {
      protect(document.body)
    }
    run()
    window.addEventListener('pb-pages-mounted', run)
    return () => {
      window.removeEventListener('pb-pages-mounted', run)
    }
  }, [])
  return null
}
